import { useState } from "react";
import { PageHeader, Card, CardHeader, AtomBadge, RiskPill, StatusPill } from "../components/primitives";
import { Search, Building2, User, ChevronRight, ChevronDown, FileDown, Repeat, ShieldAlert, X, Globe2, FileText } from "lucide-react";
import { toast } from "sonner";

// Seeded BO catalogue
const COMPANIES = {
  "adeola imports": {
    name: "Adeola Imports Ltd", rc: "RC 982341", incorp: "14 Feb 2019", address: "12 Marina St, Lagos Island, Lagos", naics: "424910 · Farm Supplies Merchant Wholesalers", status: "Active", risk: "High", cac: "Today 04:12 WAT",
    tree: {
      root: { id: "co1", kind: "co", label: "Adeola Imports Ltd", sub: "RC 982341" },
      edges: [
        { from: "co1", to: "co2", pct: "100%" },
        { from: "co2", to: "co3", pct: "60%" },
        { from: "co2", to: "p1", pct: "40%" },
        { from: "co3", to: "p2", pct: "60%" },
        { from: "co3", to: "p3", pct: "40%" },
      ],
      nodes: {
        co2: { kind: "co", label: "Adeola Holdings Ltd", sub: "RC 1162032 · Lagos" },
        co3: { kind: "co", label: "Mauritius Holdings Ltd", sub: "C 458291 · Port Louis · MU", flag: "opacity" },
        p1: { kind: "person", label: "Funmi Adeyemi", sub: "BVN ••• ••• 4421 · 40%", risk: "Medium", sanctions: "Clear", pep: "None" },
        p2: { kind: "person", label: "Olumide Adeyemi", sub: "BVN ••• ••• 7392 · 60%", risk: "Medium", sanctions: "Clear", pep: "None" },
        p3: { kind: "person", label: "Folake Adeyemi", sub: "BVN ••• ••• 8841 · 40%", risk: "High", sanctions: "Clear", pep: "PEP relative (spouse)" },
      }
    },
    ubos: [
      { name: "Olumide Adeyemi", cum: "60%", path: "via Adeola Holdings Ltd → Mauritius Holdings Ltd", sanctions: "Clear", pep: "None", risk: "Medium" },
      { name: "Folake Adeyemi", cum: "40%", path: "via Adeola Holdings Ltd → Mauritius Holdings Ltd", sanctions: "Clear", pep: "PEP relative (spouse of former state commissioner)", risk: "High" },
    ]
  },
  "vermilion": {
    name: "Vermilion Trading Solutions Ltd", rc: "RC 2208472", incorp: "08 Aug 2022", address: "Plot 84, Ajose Adeogun, Victoria Island, Lagos", naics: "423990 · Other Misc Durable Goods Wholesalers", status: "Under investigation", risk: "Very High", cac: "Yesterday 14:08 WAT",
    tree: {
      root: { id: "v1", kind: "co", label: "Vermilion Trading Solutions Ltd", sub: "RC 2208472" },
      edges: [
        { from: "v1", to: "v2", pct: "55%" },
        { from: "v1", to: "v3", pct: "45%" },
        { from: "v2", to: "v4", pct: "100%" },
        { from: "v4", to: "vp1", pct: "100%" },
        { from: "v3", to: "vp2", pct: "100%" },
      ],
      nodes: {
        v2: { kind: "co", label: "Vermilion Holdings BVI", sub: "BVI 488221 · nominee shareholders detected", flag: "opacity" },
        v3: { kind: "co", label: "Crystal Pacific Trust", sub: "Seychelles trustee", flag: "opacity" },
        v4: { kind: "co", label: "Seychelles Layer Co", sub: "SC 388291 · Mahé", flag: "opacity" },
        vp1: { kind: "person", label: "Nominee shareholder", sub: "Documented nominee — UBO undisclosed", risk: "Very High", sanctions: "Pending", pep: "Unknown" },
        vp2: { kind: "person", label: "Mr. Beneficiary (anonymous)", sub: "Trust beneficiary · identity withheld", risk: "Very High", sanctions: "Pending", pep: "Unknown" },
      }
    },
    ubos: []
  },
  "lagos marine": {
    name: "Lagos Marine Imports Ltd", rc: "RC 1389271", incorp: "22 May 2017", address: "8 Apapa Quays, Lagos", naics: "488210 · Marine Cargo Handling", status: "Active", risk: "Medium", cac: "Today 03:48 WAT",
    tree: {
      root: { id: "l1", kind: "co", label: "Lagos Marine Imports Ltd", sub: "RC 1389271" },
      edges: [
        { from: "l1", to: "lp1", pct: "55%" },
        { from: "l1", to: "lp2", pct: "30%" },
        { from: "l1", to: "lp3", pct: "15%" },
      ],
      nodes: {
        lp1: { kind: "person", label: "Chinwe Okeke", sub: "BVN ••• ••• 1132 · 55%", risk: "Low", sanctions: "Clear", pep: "None" },
        lp2: { kind: "person", label: "Tunde Bamidele", sub: "BVN ••• ••• 8842 · 30%", risk: "Low", sanctions: "Clear", pep: "None" },
        lp3: { kind: "person", label: "Patience Briggs", sub: "BVN ••• ••• 0091 · 15%", risk: "Medium", sanctions: "Clear", pep: "None" },
      }
    },
    ubos: [
      { name: "Chinwe Okeke", cum: "55%", path: "direct shareholding", sanctions: "Clear", pep: "None", risk: "Low" },
      { name: "Tunde Bamidele", cum: "30%", path: "direct shareholding", sanctions: "Clear", pep: "None", risk: "Low" },
      { name: "Patience Briggs", cum: "15%", path: "direct shareholding", sanctions: "Clear", pep: "None", risk: "Medium" },
    ]
  },
};

const findCompany = (q) => {
  const ql = q.toLowerCase().trim();
  if (!ql) return null;
  const key = Object.keys(COMPANIES).find(k => COMPANIES[k].name.toLowerCase().includes(ql) || k.includes(ql) || COMPANIES[k].rc.toLowerCase().includes(ql));
  return key ? COMPANIES[key] : null;
};

export default function BeneficialOwnership() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("company");
  const [walk, setWalk] = useState(true);
  const [intl, setIntl] = useState(true);
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  const search = () => {
    if (!q.trim()) return;
    setSearching(true);
    setResult(null);
    setTimeout(() => {
      const c = findCompany(q);
      setResult(c || { notFound: true, q });
      setSelected(null);
      setSearching(false);
      if (c) toast.success(`BO tree constructed for ${c.name}`, { description: "Powered by Atom Neutron v2.0 + Atom Nucleus v3.5" });
    }, 700);
  };

  return (
    <div data-testid="bo-page">
      <PageHeader
        title="Beneficial Ownership"
        subtitle="Search any company or beneficial owner. Atom constructs the full ownership chain to natural persons and surfaces sanctions, PEP, and adverse media on every node."
        sections={["5.2"]}
        atom={<div className="flex gap-2 flex-wrap"><AtomBadge model="Atom Neutron" version="v2.0" description="BO retrieval & tree construction" /><AtomBadge model="Atom Nucleus" version="v3.5" description="Network inference & obscuration detection" /></div>}
      />

      <div className="p-7 space-y-5">
        {/* Search workspace */}
        <Card>
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 border-2 border-line rounded-lg px-3 py-2.5 focus-within:border-amber-600 transition-colors">
              <Search size={16} className="text-ink-subtle" />
              <input data-testid="bo-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Enter company name, RC number, or beneficial owner name (e.g., Adeola Imports, Vermilion, Lagos Marine)" className="flex-1 bg-transparent text-[14px] outline-none" />
              <button data-testid="bo-search-btn" onClick={search} className="px-4 py-1.5 text-[12px] bg-navy text-white rounded-md hover:bg-amber-600 font-medium">Search</button>
            </div>
            <div className="flex items-center gap-4 flex-wrap text-[11px]">
              <div className="inline-flex border border-line rounded-md p-0.5 bg-bg-warm/40">
                {[["company", "Search by Company"], ["owner", "Search by Owner"]].map(([k, l]) => (
                  <button key={k} onClick={() => setType(k)} className={`px-2.5 py-1 rounded transition-colors ${type === k ? "bg-bg-surface text-ink font-medium shadow-sm" : "text-ink-muted hover:text-ink"}`}>{l}</button>
                ))}
              </div>
              <label className="inline-flex items-center gap-1.5 text-ink-muted"><input type="checkbox" checked={walk} onChange={(e) => setWalk(e.target.checked)} className="w-3.5 h-3.5" />Construct full ownership tree</label>
              <label className="inline-flex items-center gap-1.5 text-ink-muted"><input type="checkbox" checked={intl} onChange={(e) => setIntl(e.target.checked)} className="w-3.5 h-3.5" />Include international entities (D&B + GLEIF)</label>
            </div>
          </div>
        </Card>

        {searching && (
          <Card><div className="p-8 text-center text-[12px] text-ink-muted">
            <div className="inline-block w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full animate-spin mr-2" />
            {intl && <span>Querying CAC, Dun &amp; Bradstreet, and GLEIF · </span>}Constructing ownership chain with Atom Neutron v2.0…
          </div></Card>
        )}

        {result && result.notFound && (
          <Card><div className="p-8 text-center">
            <div className="text-[14px] text-ink font-medium">No entity matches "{result.q}"</div>
            <div className="text-[12px] text-ink-muted mt-1">Try: <button className="text-navy underline" onClick={() => { setQ("Adeola Imports"); }}>Adeola Imports</button> · <button className="text-navy underline" onClick={() => setQ("Vermilion")}>Vermilion</button> · <button className="text-navy underline" onClick={() => setQ("Lagos Marine")}>Lagos Marine</button></div>
          </div></Card>
        )}

        {result && !result.notFound && (
          <>
            {/* Company header */}
            <Card>
              <div className="p-5 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 size={18} className="text-navy" strokeWidth={1.5} />
                    <h2 className="display text-[20px] font-semibold text-ink">{result.name}</h2>
                    <StatusPill status={result.status} />
                    <RiskPill band={result.risk} />
                  </div>
                  <div className="grid grid-cols-4 gap-x-6 gap-y-1.5 mt-3 text-[12px]">
                    {[["RC number", result.rc], ["Incorporation date", result.incorp], ["Business class.", result.naics], ["Registered address", result.address], ["Last CAC sync", result.cac], ["Status", result.status]].map(([k, v]) => (
                      <div key={k}><div className="text-ink-subtle text-[10px] uppercase tracking-wide">{k}</div><div className="text-ink">{v}</div></div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Tree + side panel */}
            <div className="grid grid-cols-[1fr_360px] gap-4">
              <Card>
                <CardHeader title="Ownership tree" subtitle="Click any node to inspect detail · edges show ownership percentage" right={<AtomBadge model="Atom Neutron" version="v2.0" />} />
                <div className="p-4 overflow-x-auto">
                  <BOTree tree={result.tree} onSelect={setSelected} selectedId={selected?.id} />
                </div>
              </Card>
              <Card>
                <CardHeader title={selected ? "Node detail" : "Select a node"} />
                <div className="p-4 text-[12px]">
                  {!selected && <div className="text-ink-muted">Click any node in the tree to view full profile, sanctions screening, PEP status, and adverse media.</div>}
                  {selected && selected.kind === "person" && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-amber/15 border border-amber/30 flex items-center justify-center text-amber-700 text-[16px] font-medium">{selected.label.split(" ").map(w => w[0]).slice(0, 2).join("")}</div>
                        <div><div className="font-medium text-ink">{selected.label}</div><div className="text-ink-subtle text-[11px]">{selected.sub}</div></div>
                      </div>
                      <dl className="space-y-1.5 border-t border-line pt-3">
                        {[["Risk band", selected.risk], ["Sanctions", selected.sanctions], ["PEP status", selected.pep], ["Adverse media", "0 high-confidence hits"], ["Other directorships", "2 companies"]].map(([k, v]) => (
                          <div key={k} className="flex justify-between"><dt className="text-ink-subtle">{k}</dt><dd className="text-ink text-right">{v}</dd></div>
                        ))}
                      </dl>
                    </div>
                  )}
                  {selected && selected.kind === "co" && (
                    <dl className="space-y-1.5">
                      {[["Entity", selected.label], ["Sub", selected.sub], ["Filings up to date", "Yes"], ["Directors", "3 listed"], ["Adverse media", selected.flag === "opacity" ? "Flagged — opacity jurisdiction" : "0 hits"]].map(([k, v]) => (
                        <div key={k} className="flex justify-between"><dt className="text-ink-subtle">{k}</dt><dd className="text-ink text-right">{v}</dd></div>
                      ))}
                      {selected.flag === "opacity" && <div className="mt-2 text-[11px] bg-amber/10 border border-amber/30 text-amber-700 rounded p-2 inline-flex items-start gap-1.5"><ShieldAlert size={12} className="mt-0.5" />Opacity jurisdiction detected by Atom Nucleus v3.5. Recommend Enhanced Due Diligence.</div>}
                    </dl>
                  )}
                </div>
              </Card>
            </div>

            {/* UBO summary */}
            <Card>
              <CardHeader title={`Ultimate beneficial owners identified — ${result.ubos.length}`} subtitle="Resolved through full chain walk" right={<AtomBadge model="Atom Nucleus" version="v3.5" description="Network inference" />} />
              <div>
                {result.ubos.length === 0 && (
                  <div className="px-5 py-6 bg-risk-veryhigh/5 border-l-4 border-risk-veryhigh">
                    <div className="text-[13px] text-ink font-medium">No natural-person UBO resolved.</div>
                    <div className="text-[12px] text-ink-muted mt-1">Atom Nucleus v3.5 detected nominee shareholders and opacity-jurisdiction layers (BVI, Seychelles). Mandatory Enhanced Due Diligence triggered. RULE-BO-001 alert active.</div>
                  </div>
                )}
                {result.ubos.map((u, i) => (
                  <div key={i} className="px-5 py-3 border-b border-line last:border-0 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[13px] font-medium text-ink">{u.name}</div>
                      <div className="text-[11px] text-ink-subtle">{u.cum} cumulative ownership · {u.path}</div>
                    </div>
                    <div className="flex gap-2 text-[11px]">
                      <span className="text-ink-muted">Sanctions: <span className="text-risk-low font-medium">{u.sanctions}</span></span>
                      <span className="text-ink-muted">PEP: <span className={u.pep === "None" ? "text-ink" : "text-amber-600 font-medium"}>{u.pep}</span></span>
                      <RiskPill band={u.risk} size="sm" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              <button data-testid="gen-pdf" onClick={() => { toast.success("Generating full BO report (PDF)…", { description: "Multi-page report queued — will appear in Documents in 10s" }); }} className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-md hover:bg-amber-600 inline-flex items-center gap-1.5"><FileText size={12} />Generate full BO report (PDF)</button>
              <button onClick={() => toast.success("Ownership data exported (XLSX)")} className="px-3 py-1.5 text-[12px] border border-line rounded-md text-ink-muted hover:bg-bg-warm inline-flex items-center gap-1.5"><FileDown size={12} />Export ownership data (XLSX)</button>
              <button onClick={() => toast.success("Quarterly review scheduled")} className="px-3 py-1.5 text-[12px] border border-line rounded-md text-ink-muted hover:bg-bg-warm inline-flex items-center gap-1.5"><Repeat size={12} />Create periodic review schedule</button>
              <button onClick={() => toast.success("Flagged for Enhanced Due Diligence")} className="px-3 py-1.5 text-[12px] border border-amber/40 text-amber-600 rounded-md hover:bg-amber/8 inline-flex items-center gap-1.5"><ShieldAlert size={12} />Flag for EDD</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Simple horizontal tree renderer (recursive levels)
function BOTree({ tree, onSelect, selectedId }) {
  const childrenOf = (id) => tree.edges.filter(e => e.from === id);
  const nodeFor = (id) => id === tree.root.id ? tree.root : tree.nodes[id];

  const renderLevel = (id, depth = 0) => {
    const node = nodeFor(id);
    const children = childrenOf(id);
    const isSelected = selectedId === id;
    return (
      <div className="flex items-stretch gap-6" key={id}>
        <button data-testid={`bo-node-${id}`} onClick={() => onSelect({ id, ...node })} className={`shrink-0 self-start text-left rounded-lg border-2 px-3 py-2.5 transition-colors ${isSelected ? "border-amber-600 bg-amber/8" : node.kind === "co" ? "border-navy/30 bg-navy/4 hover:border-navy" : "border-line bg-bg-surface hover:border-amber-600"} ${node.flag === "opacity" ? "ring-2 ring-amber/40" : ""}`}>
          <div className="flex items-center gap-1.5">
            {node.kind === "co" ? <Building2 size={12} className="text-navy" strokeWidth={1.5} /> : <User size={12} className="text-amber-700" strokeWidth={1.5} />}
            <div className="text-[12px] font-medium text-ink truncate max-w-[180px]">{node.label}</div>
          </div>
          <div className="text-[10px] text-ink-subtle mt-0.5 truncate max-w-[200px]">{node.sub}</div>
          {node.flag === "opacity" && <div className="text-[9px] text-amber-700 mt-1 inline-flex items-center gap-0.5"><ShieldAlert size={9} />opacity jurisdiction</div>}
        </button>
        {children.length > 0 && (
          <div className="flex flex-col gap-3 justify-center">
            {children.map((e) => (
              <div key={e.to} className="flex items-center gap-2">
                <div className="flex flex-col items-center">
                  <div className="text-[10px] text-amber-700 font-medium tabular -mb-0.5">{e.pct}</div>
                  <div className="h-px w-10 bg-line"></div>
                </div>
                {renderLevel(e.to, depth + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return <div className="min-w-fit py-2">{renderLevel(tree.root.id)}</div>;
}
